import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { useLocation, useNavigate } from 'react-router-dom';
import PhotoSwipe from 'photoswipe';
import { GetPhotoGalleryResponse } from '../../../../../../shareables/types/api/main/website/page/type/206/photo-gallery/get';
import Upsert from './upsert';
import { GetMyPhotosResponse } from '../../../../../../shareables/types/api/main/website/page/type/206/my-photos/get';


// Styled components
const Container = styled.div`
	.pswp__custom-caption {
		position: absolute;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.3);
		width: 100%;
		text-align: center;
		font-size: 0.8125rem;
		color: #ccc;
		padding-bottom: 0.875rem;
		padding-top: 0.6875rem;
		min-height: 2.75rem;
	}
`;

const Wrapper = styled.div`
	margin-top: -0.0938rem;
	
	div {
		margin-top: -0.0938rem;
	}
`;

const Gallery = styled.div`
	img {
		height: 9.375rem;
		max-width: min(25rem, 100%);
		margin: 0 0.75rem 0.3438rem 0;
		object-fit: cover;
	}
`;

const NoneAdded = styled.div`
	margin-top: -0.0938rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookPhotoGallery?: GetPhotoGalleryResponse;
	storybookActivePanel?: 'add' | 'manage';
	storybookShowPanel?: true;
	storybookMyPhotos?: GetMyPhotosResponse;
}


// Function component
const PhotoGallery: React.FC<Props> = ({
	pageID,
	storybookPhotoGallery,
	storybookActivePanel,
	storybookShowPanel,
	storybookMyPhotos,
}) => {
	// Use state
	const [photoGallery, setPhotoGallery] = useState<GetPhotoGalleryResponse | null>(storybookPhotoGallery ?? null);
	const [indexFromRouteHash, setIndexFromRouteHash] = useState<number | null>(null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Function that loads photos
	const memoizedLoadPhotos = useCallback(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetPhotoGalleryResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/206/photo-gallery`,
				completion: (json) => {
					setPhotoGallery(json.response);
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// Load photos on mount
	useEffect(() => {
		memoizedLoadPhotos();
	}, [memoizedLoadPhotos]);
	
	
	// Use ref
	const lightboxRef = useRef<PhotoSwipeLightbox | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const usedBrowserNavigation = useRef(false);
	
	
	// Use React Router functionality
	const { hash } = useLocation();
	const navigate = useNavigate();
	
	
	// Track browser back/forward button usage
	useEffect(() => {
		const handlePopstate = () => {
			usedBrowserNavigation.current = true;
		};
		
		window.addEventListener('popstate', handlePopstate);
		
		return () => window.removeEventListener('popstate', handlePopstate);
	}, []);
	
	
	// Create lightbox
	useEffect(() => {
		// Return early
		if (!photoGallery || photoGallery.photos.length === 0 || !containerRef.current || lightboxRef.current) {
			return;
		}
		
		
		// Create instance
		lightboxRef.current = new PhotoSwipeLightbox({
			gallery: '#gallery',
			children: 'a',
			pswpModule: PhotoSwipe,
			bgOpacity: 0.85,
			padding: { top: 44, right: 0, bottom: 44, left: 0 },
			appendToEl: containerRef.current,
		});
		
		
		// Add captions
		lightboxRef.current.on('uiRegister', () => {
			const pswp = lightboxRef.current?.pswp;
			
			pswp?.ui?.registerElement({
				name: 'custom-caption',
				order: 9,
				isButton: false,
				appendTo: 'wrapper',
				html: 'Caption text',
				onInit: (el) => {
					pswp.on('change', () => {
						el.textContent = photoGallery.photos[pswp.currIndex].caption ?? '';
					});
				},
			});
		});
		
		
		// Add hash to route when lightbox is opened or slide changes normally
		//  -> The if statement prevents pushing a new history state and overriding the browser history
		lightboxRef.current.on('change', () => {
			if (!usedBrowserNavigation.current) {
				navigate(`#&gid=1&pid=p${photoGallery.photos[lightboxRef.current?.pswp?.currIndex ?? 0].id}`, {
					state: { disableScrollToTop: true },
				});
			}
			
			usedBrowserNavigation.current = false;
		});
		
		
		// Remove hash when lightbox is closed normally
		//  -> The if statement prevents pushing a new history state and overriding the browser history
		lightboxRef.current.on('close', () => {
			if (!usedBrowserNavigation.current) {
				navigate('#', { state: { disableScrollToTop: true } });
			}
			
			usedBrowserNavigation.current = false;
		});
		
		
		// Initialize
		lightboxRef.current.init();
		
		
		// Clean up
		return () => {
			if (lightboxRef.current) {
				lightboxRef.current.destroy();
				lightboxRef.current = null;
			}
		};
	}, [navigate, photoGallery]);
	
	
	// Extract photo ID or index from route hash and set the index
	useEffect(() => {
		// Return early
		if (!photoGallery || photoGallery.photos.length === 0) {
			return;
		}
		
		
		// Extract pid from route hash
		const match = hash.match(/pid=([a-zA-Z0-9]+)/);
		const hashPID = match ? match[1] : null;
		
		
		// Return early if no pid found
		if (!hashPID) {
			setIndexFromRouteHash(null);
			return;
		}
		
		
		// Initialize
		let newIndex = 0;
		
		
		// Check if photo ID was provided
		//  -> photo ID's are prefixed with 'p'
		if (hashPID.startsWith('p')) {
			// Attempt to extract photo ID as a number
			const photoID = parseInt(hashPID.slice(1));
			
			
			// Check if number
			if (!isNaN(photoID)) {
				// Try to find the corresponding index
				newIndex = photoGallery.photos.findIndex((photo) => photo.id === photoID);
			}
		} else {
			// Attempt to convert hash pid to number
			const potentialIndex = parseInt(hashPID);
			
			
			// Ensure index is within bounds
			if (!isNaN(potentialIndex) && potentialIndex >= 1 && potentialIndex <= photoGallery.photos.length) {
				// Convert to 0-based index
				newIndex = potentialIndex - 1;
			}
		}
		
		
		// If no valid index is found, default to the first photo
		if (newIndex === -1) {
			newIndex = 0;
		}
		
		
		// Update state
		setIndexFromRouteHash(newIndex);
	}, [hash, photoGallery]);
	
	
	// Open the lightbox at the specified index
	useEffect(() => {
		// Return early
		if (!photoGallery || photoGallery.photos.length === 0 || !lightboxRef.current) {
			return;
		}
		
		
		// Destructure to access PhotoSwipe core methods
		const { pswp } = lightboxRef.current;
		
		
		// Handle no hash
		if (indexFromRouteHash === null) {
			if (pswp?.isOpen) {
				pswp.close();
			}
			
			return;
		}
		
		
		// Check if lightbox is opened
		if (pswp?.isOpen) {
			// Display the requested photo in the lightbox
			if (pswp.currIndex !== indexFromRouteHash) {
				pswp.goTo(indexFromRouteHash);
			}
			
			
			// Return
			return;
		}
		
		
		// Open lightbox and display the requested photo
		lightboxRef.current.loadAndOpen(indexFromRouteHash);
	}, [photoGallery, indexFromRouteHash]);
	
	
	// If loading, display nothing
	if (photoGallery === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<Container ref={containerRef}>
			{photoGallery.allowMemberUploads && (
				<Upsert
					pageID={pageID}
					onChange={memoizedLoadPhotos}
					storybookActivePanel={storybookActivePanel}
					storybookShowPanel={storybookShowPanel}
					storybookMyPhotos={storybookMyPhotos}
				/>
			)}
			
			{photoGallery.photos.length > 0 ? (
				<Wrapper>
					<em>Click any photo to enlarge it!</em>
					<Gallery className='pswp-gallery' id='gallery'>
						{photoGallery.photos.map((photo, i) => (
							<a
								href={photo.urls.full}
								data-pswp-width={photo.size.width}
								data-pswp-height={photo.size.height}
								key={photo.id}
								target='_blank'
								rel='noreferrer'
							>
								<img src={photo.urls.thumbnail} alt={photo.caption ?? `Photo ${i + 1}`} />
							</a>
						))}
					</Gallery>
				</Wrapper>
			) : (
				<NoneAdded>
					<em>No photos yet…</em>
				</NoneAdded>
			)}
		</Container>
	);
};

export default PhotoGallery;
